<template>
  <v-container fluid class="list-page">
    <Create :createDialog="createDialog" @close="createDialog = false" @submit="getList" />
    <Edit :editDialog="editDialog" :editLead="selectedLead" @close="editDialog = false" @submit="getList" />

    <div class="list-page-heading">
      <div>
        <div class="list-page-heading__eyebrow">Admissions</div>
        <h1>Applications</h1>
        <p>Review prospective students, family contacts, requested classes, and follow-up status.</p>
      </div>
      <div class="list-page-heading__actions">
        <v-btn outlined color="primary" @click="print"><v-icon left>mdi-printer-outline</v-icon>Print</v-btn>
        <v-btn v-if="checkRightStatus(13)" color="primary" depressed @click="add">
          <v-icon left>mdi-plus</v-icon>New application
        </v-btn>
      </div>
    </div>

    <v-row class="list-metrics">
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-clipboard-text-outline</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ count }}</div>
            <div class="list-metric__label">Applications in result</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-human-male</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ applicationSummary.male }}</div>
            <div class="list-metric__label">Boys interested</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-human-female</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ applicationSummary.female }}</div>
            <div class="list-metric__label">Girls interested</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-account-clock-outline</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ applicationSummary.readyToJoin }}</div>
            <div class="list-metric__label">Ready to join</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card :loading="loading" elevation="0" class="list-card">
      <div class="list-toolbar">
        <v-text-field
          v-model="search"
          class="list-toolbar__search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search by student, parent, phone, or application ID"
          single-line
          hide-details
          clearable
        />
        <div class="list-toolbar__actions">
        <ClassesDropbox
          class="list-toolbar__control"
          @classSelected="classSelected"
          :ClassName="ClassName"
        />
        <v-select
          class="list-toolbar__control" v-model="selectedStatus" :items="leadStatus" label="Application status" multiple outlined dense
          hide-details @change="getList">
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 1" small color="green lighten-5" text-color="green darken-2">{{ item }}</v-chip>
            <span v-if="index === 1" class="grey--text text-caption">+{{ selectedStatus.length - 1 }} more</span>
          </template>
        </v-select>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon outlined color="primary"
              :aria-label="openNewTab ? 'Applications open in a new tab' : 'Applications open in this tab'"
              @click="openNewTab = !openNewTab">
              <v-icon size="19">{{ openNewTab ? "mdi-open-in-new" : "mdi-open-in-app" }}</v-icon>
            </v-btn>
          </template>
          <span>{{ openNewTab ? "Applications open in a new tab" : "Applications open in this tab" }}</span>
        </v-tooltip>

        <v-btn
          icon
          outlined
          color="primary"
          :loading="loading"
          aria-label="Refresh applications"
          @click="getList"
        >
          <v-icon size="19">mdi-refresh</v-icon>
        </v-btn>
        </div>
      </div>

      <v-data-table :server-items-length="count" :footer-props="{ 'items-per-page-options': [10, 25, 35, 50] }"
        class="list-table list-table--responsive" :headers="headers" :items="Leads" :loading="loading" :options.sync="options">
        <template v-slot:item="{ item }">
          <tr class="list-table-row">
            <td data-label="Application ID" @click="profile(item)"><span class="list-record-id">#<SearchHighlight :text="item.localId" :query="search" /></span></td>
            <td data-label="Received" @click="profile(item)">
              <div class="list-cell-primary">{{ item.createdAt | moment("MMM D, YYYY") }}</div>
              <div class="list-cell-secondary">{{ item.createdAt | moment("from", "now") }}</div>
            </td>
            <td data-label="Student" @click="profile(item)">
              <div class="student-name"><v-avatar size="32" color="green lighten-5" class="mr-2">
                <v-icon small color="primary">mdi-school-outline</v-icon></v-avatar>
                <span><strong><SearchHighlight :text="item.fullName" :query="search" /></strong>
                  <small>{{ getAge(item.dob) }} years old · {{ item.gender || "Not specified" }}</small>
                  <small v-if="item.phoneNumber"><SearchHighlight :text="item.phoneNumber" :query="search" /></small></span>
              </div>
            </td>
            <td data-label="Parent" @click="profile(item)">
              <div class="list-cell-primary" v-if="item.Lead.isChild"><SearchHighlight :text="item.Lead.fatherName" :query="search" /></div>
              <div class="list-cell-secondary" v-if="item.Lead.isChild && item.Lead.motherName"><SearchHighlight :text="item.Lead.motherName" :query="search" /></div>
              <div class="list-cell-secondary" v-if="!item.Lead.isChild">Applicant is an adult</div>
            </td>
            <td data-label="Phone" @click="profile(item)" class="contact-cell">
              <div><small>Dad</small><span><SearchHighlight :text="item.Lead.fatherPhoneNumber" :query="search" /></span></div>
              <div><small>Mom</small><span v-if="item.Lead.motherPhoneNumber"><SearchHighlight :text="item.Lead.motherPhoneNumber" :query="search" /></span><span v-else>—</span></div>
            </td>
            <td data-label="Requested class" @click="profile(item)">
              <span v-if="item.Class" class="list-cell-primary"><SearchHighlight :text="item.Class.name" :query="search" /></span>
              <span v-else class="list-cell-secondary">Not assigned</span>
            </td>
            <td data-label="Status" @click="profile(item)">
              <v-chip small label class="font-weight-bold text-capitalize" :class="statusClass(item.status)">{{ item.status }}</v-chip>
            </td>
            <td data-label="Actions" class="text-right action-cell">
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }"><v-btn v-bind="attrs" v-on="on" icon aria-label="Application actions">
                  <v-icon>mdi-dots-horizontal</v-icon></v-btn></template>
                <v-list class="menu-button">
                  <v-list-item @click="profile(item)"><v-list-item-title>View application</v-list-item-title></v-list-item>
                  <v-list-item v-if="checkRightStatus(15)" @click="editLead(item.Lead)"><v-list-item-title>Edit application</v-list-item-title></v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>
        <template v-slot:no-data><div class="list-empty-state py-12"><v-icon size="48" color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
          <strong>No applications found</strong><span>New student applications will appear here.</span></div></template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="showPdf" max-width="900">
      <v-card v-draggable-dialog class="pdf-dialog">
        <div class="dialog-drag-handle pdf-header"><div><strong>Application report</strong><span>Printable application list</span></div>
          <v-btn icon @click.stop="showPdf = false"><v-icon>mdi-close</v-icon></v-btn></div>
        <v-divider />
        <embed id="embedPDF" :src="pdf" type="application/pdf" height="760" width="100%" />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import leadService from "../service";
import ClassesDropbox from "@/modules/Classes/components/Dropbox.vue";
import SearchHighlight from "@/modules/ClassRoom/components/SearchHighlight.vue";
import { mapGetters } from "vuex";
import _ from 'lodash';

const Create = () => import("./Create");
const Edit = () => import("./Edit");
import pdfMake from 'pdfmake/build/pdfmake.min';
import { pdfMake as pdfFonts } from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.vfs;

export default {
  name: "lead-list",
  data() {
    return {
      leadStatus: ["pending", "Open", "Contacted", "Waiting", "Closed", "Convert", "Ready to join"],
      openNewTab: true,
      createDialog: false,
      editDialog: false,
      count: 0,
      selectedLead: null,
      headers: [
        {
          text: "#",
          align: "left",
          value: "localId",
          sortable: true,
        },
        {
          text: "Received",
          align: "left",
          value: "createdAt",
          sortable: true,
        },
        {
          text: "Student Name",
          align: "left",
          value: "firstName",
          sortable: true,
        },
        {
          text: "Parent",
          value: "Lead.fatherName",
          sortable: false,
        },
        {
          text: "Phone",
          align: "left",
          value: "Lead.fatherPhoneNumber",
          sortable: false,
        },
        {
          text: "Requested Class",
          align: "left",
          value: "Class.name",
          sortable: false,
        },
        {
          text: "Status",
          value: "status",
          sortable: false,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      Leads: [],
      selectedStatus: ["pending", "Open", "Waiting", "Contacted", "Ready to join"],
      search: '',
      loading: true,
      selectedClasses: [],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [false]
      },
      doc: {
        content: []
      },
      ClassName: null,
      pdf: null,
      showPdf: false,
      listRequestId: 0,
      applicationSummary: {
        male: 0,
        female: 0,
        readyToJoin: 0,
      },
    };
  },
  watch: {
    search: {
      handler: _.debounce(function () {
        if (this.options.page !== 1) this.options.page = 1;
        else this.getList();
      }, 500),
    },
    options: {
      handler() {
        if(!this.loading)
          this.getList();
      },
      deep: true,
    },
  },
  created() {
    if (this.$route.query) {
      if(this.$route.query.search)
        this.search = this.$route.query.search
      if(this.$route.query.ClassId)
      this.selectedClasses = [this.$route.query.ClassId]
    if(this.$route.query.ClassName)
      this.ClassName = this.$route.query.ClassName
    }
    this.getList();
    document.title = "taleem - Application";
  },
  components: {
    Create,
    Edit,
    ClassesDropbox,
    SearchHighlight
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus", "currentBusiness"]),
  },
  methods: {
    classSelected(val) {
      this.selectedClasses = val
      this.getList()
    },
    getAge(dateString) {
      var today = new Date();
      var birthDate = new Date(dateString);
      var age = today.getFullYear() - birthDate.getFullYear();
      var m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },
    statusColor(status) {
      let color = 'green'
      switch (status) {
        case 'Open':
          color = '#1976D2'
          break;
        case 'Contacted':
          color = '#6A1B9A'
          break;
        case 'Closed':
          color = 'red'
          break;
        case 'Waiting':
          color = '#AED581'
          break;
        case 'Ready to join':
          color = '#00BFA5'
          break;
        default:
          color = 'green'
      }

      return color
    },
    statusClass(status) {
      if (status === "Ready to join" || status === "Convert") return "list-status--ready";
      if (status === "Closed") return "list-status--closed";
      if (status === "Waiting") return "list-status--waiting";
      if (status === "Contacted") return "list-status--contacted";
      return "list-status--open";
    },
    profile(row) {
      sessionStorage.setItem(
        "applicationProfileQueue",
        JSON.stringify(this.Leads.map((item) => ({ id: item.Lead.id, name: item.fullName })))
      );
      const route = this.$router.resolve({ path: `/applications/${row.Lead.id}/profile` });
      if (this.openNewTab) {
        window.open(route.href, "_blank");
      } else {
        this.$router.push({ path: `/applications/${row.Lead.id}/profile` });
      }
    },
    add() {
      this.createDialog = !this.createDialog;
    },
    getList() {
      const requestId = ++this.listRequestId;
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      let offset = 0
      if (itemsPerPage > 0 && page) {
        offset = itemsPerPage * page - itemsPerPage;
      }
      let where = {
        status: this.selectedStatus,
        filteredClasses: this.selectedClasses,
        limit: itemsPerPage,
        sortBy: sortBy[0],
        sortDesc: sortDesc[0],
      };
      if (this.search && this.search.length >= 1) {
        where.search = this.search
      } else {
        where.offset = offset
      }
      return leadService
        .getAllStudent(where)
        .then((response) => {
          if (requestId !== this.listRequestId) return response;
          this.loading = false;
          this.Leads = response.data;
          this.count = response.count;
          this.applicationSummary = response.summary || {
            male: 0,
            female: 0,
            readyToJoin: 0,
          };
          return response;
        });
    },
    editLead(lead) {
      this.selectedLead = lead;
      this.editDialog = !this.editDialog;
    },
    deleteLead(lead) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          leadService.delete(lead.id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Lead has been deleted.", "success");
              this.getList();
            }
          });
        }
      });
    },
    async print() {
      this.doc.content = []
      if (this.currentBusiness && this.currentBusiness.imageUrl) {
        try {
          const toDataURL = (url) =>
            fetch(url, { mode: 'cors' })
              .then((response) => response.blob())
              .then(
                (blob) =>
                  new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                  }),
              );

          const base64 = await toDataURL(this.currentBusiness.imageUrl).then(
            (dataUrl) => dataUrl,
          );

          this.doc.content.push({
            image: base64,
            width: 200,
            height: 100,
            alignment: 'center',
          });
        } catch (error) {
          console.log('image through error');
        }
      }
      this.doc.content.push({
        text: this.currentBusiness.name,
        alignment: 'center',
        fontSize: 15,
        bold: true,
      });
      this.doc.content.push({
        text: `${this.currentBusiness.name} ${this.currentBusiness.address} ${this.currentBusiness.city}, ${this.currentBusiness.state} ${this.currentBusiness.zip}`,
        alignment: 'center',
        fontSize: 11,
      });
      this.doc.content.push({
        text: this.currentBusiness.phoneNumber,
        alignment: 'center',
        fontSize: 11,
      });
      this.doc.content.push({
        text: this.currentBusiness.email,
        alignment: 'center',
        fontSize: 11,
      });
      this.doc.content.push({
        text: this.currentBusiness.website,
        alignment: 'center',
        fontSize: 11,
      });
      this.doc.content.push({
        columns: [
          { text: "Application", alignment: 'left', fontSize: 18, bold: true, margin: [2, 2] },
          { text: "Total Applications: " + this.count, alignment: 'right', fontSize: 16, bold: true, margin: [2, 2] }
        ]
      })
      let applications = [[{ text: 'CreatedAt', bold: true }, { text: 'StudentName', bold: true }, { text: 'FatherName', bold: true }, { text: 'PhoneNumber', bold: true }, { text: 'Class', bold: true }, { text: 'Status', bold: true }]]
      this.Leads.map(lead => {
        applications.push([{ text: `${this.$moment(lead.createdAt).format('L LT')}` },
        { text: `${lead.fullName}(${this.getAge(lead.dob)})` }, { text: lead.Lead.fatherName },
        { text: lead.Lead.motherPhoneNumber ? `${lead.Lead.fatherPhoneNumber}\n${lead.Lead.motherPhoneNumber}` : lead.Lead.fatherPhoneNumber, alignment: 'center' },
        { text: lead.Class ? lead.Class.name : "Not assigned" }, { text: lead.status, bold: true }
        ])
      })
      let borderStyle = {
        hLineWidth: function (i, node) {
          if (i === 0 || i === 1 || i === node.table.body.length - 1 || i === node.table.body.length) {
            return 2;
          }
          return 1;
        },
        vLineWidth: function (i, node) {
          if (i === 0 || i === node.table.widths.length) {
            return 2;
          }
          return 1;
        },
        hLineColor: function () {
          return 'black';
        },
        vLineColor: function () {
          return 'black';
        }
      };
      this.doc.content.push({
        table: {
          margin: [0, 6, 0, 0],
          widths: ['auto', '*', '*', '*', '*', 'auto'],
          body: applications
        },
        layout: borderStyle
      })
      pdfMake.createPdf(this.doc).getDataUrl(data => {
        this.pdf = data;
      });
      this.showPdf = true
    },
  },
};
</script>

<style scoped>
/* Only application-specific styles remain here. Shared list styles live in app.scss. */

.student-name {
  display: flex;
  align-items: center;
}

.student-name span {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.student-name strong {
  color: #273247;
}

.student-name small {
  color: #929d95;
  text-transform: capitalize;
}

.contact-cell > div {
  display: grid;
  grid-template-columns: 30px 1fr;
  gap: 5px;
}

.contact-cell small {
  color: #8b978e;
  font-size: 0.62rem;
  font-weight: 800;
  text-transform: uppercase;
}

.pdf-dialog {
  overflow: hidden;
  border-radius: 12px !important;
}

.pdf-header {
  display: flex;
  min-height: 68px;
  padding: 14px 18px;
  align-items: center;
  justify-content: space-between;
  cursor: move;
}

.pdf-header > div {
  display: flex;
  flex-direction: column;
  color: #273247;
}

.pdf-header span {
  color: #929d95;
  font-size: 0.75rem;
}

</style>
